/**
 * Crear o editar una plantilla de documento.
 *
 * Tres bloques, en el orden en que se piensan:
 *   1. Qué documento es: nombre y tipo. Del tipo salen la firma y la carpeta, y
 *      se muestran en una línea para que no haya que adivinarlo.
 *   2. Qué cambia en cada envío: los campos del envío (capacitación, relator…).
 *   3. El texto, con «Insertar dato», y su vista previa con una persona real.
 *
 * Rutas: `/payroll/documents/templates/new` (con `?ejemplo=` o `?copiar=`) y
 * `/payroll/documents/templates/:id`.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Eye, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/Button'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { useToast, ToastContainer } from '@/components/Toast'
import { useVisorPdf } from '@/components/VisorPdf'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useDocumentTypes } from '../../hooks/useHrDocuments'
import {
  pedirVistaPrevia, useCandidatos, useGuardarPlantilla, usePlantilla, useVariablesDePlantilla,
  type CampoDelEnvio, type EstadoPlantilla, type VariableDePlantilla,
} from '../../hooks/usePlantillasDocumentos'
import { EditorTexto } from './EditorTexto'
import { EJEMPLOS } from './ejemplos'
import { claveDesdeRotulo } from './utilidades'

interface Borrador {
  name: string
  document_type: string
  content_html: string
  envio_fields: CampoDelEnvio[]
  status: EstadoPlantilla
}

const VACIO: Borrador = { name: '', document_type: '', content_html: '', envio_fields: [], status: 'draft' }

export default function EditorPlantillaPage() {
  const { id } = useParams()
  const esNueva = !id || id === 'new'
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()

  const { data: guardada } = usePlantilla(esNueva ? undefined : id)
  const { data: original } = usePlantilla(esNueva ? params.get('copiar') ?? undefined : undefined)
  const { data: tipos = [] } = useDocumentTypes()
  const { data: catalogo = [] } = useVariablesDePlantilla()
  const guardar = useGuardarPlantilla(esNueva ? undefined : id)

  const [b, setB] = useState<Borrador>(VACIO)
  const [sucio, setSucio] = useState(false)
  const [avisos, setAvisos] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [vista, setVista] = useState<'editor' | 'previa'>('editor')
  const iniciado = useRef(false)

  // Se inicializa UNA vez: después manda lo que se escribe, no lo que vuelve
  // del servidor (que llega saneado y movería el cursor del editor).
  useEffect(() => {
    if (iniciado.current) return
    if (!esNueva && guardada) {
      setB({ name: guardada.name, document_type: guardada.document_type, content_html: guardada.content_html,
        envio_fields: guardada.envio_fields, status: guardada.status })
      iniciado.current = true
    } else if (esNueva && params.get('copiar') && original) {
      setB({ name: `Copia de ${original.name}`, document_type: original.document_type,
        content_html: original.content_html, envio_fields: original.envio_fields, status: 'draft' })
      iniciado.current = true
    } else if (esNueva && !params.get('copiar')) {
      const ejemplo = EJEMPLOS.find((e) => e.id === params.get('ejemplo'))
      if (ejemplo) setB({ ...VACIO, name: ejemplo.name, content_html: ejemplo.content_html, envio_fields: ejemplo.envio_fields })
      iniciado.current = true
    }
  }, [esNueva, guardada, original, params])

  const cambiar = (parcial: Partial<Borrador>) => { setB((x) => ({ ...x, ...parcial })); setSucio(true) }
  const tipo = tipos.find((t) => t.id === b.document_type)

  const variables: VariableDePlantilla[] = useMemo(() => [
    ...catalogo.filter((v) => !v.key.startsWith('envio.')),
    ...b.envio_fields.map((c) => ({ key: `envio.${c.key}`, label: c.label || c.key, group: 'Campos del envío', type: 'texto' as const })),
  ], [catalogo, b.envio_fields])

  const enviar = (status: EstadoPlantilla) => {
    setError(null)
    guardar.mutate({ ...b, status }, {
      onSuccess: (p) => {
        setSucio(false)
        setB((x) => ({ ...x, status: p.status }))
        setAvisos(p.content_warnings ?? [])
        toast.success(status === 'active' ? 'Plantilla guardada y activa' : 'Plantilla guardada')
        if (esNueva) navigate(`/payroll/documents/templates/${p.id}`, { replace: true })
      },
      onError: (e) => setError(extractErrorMessage(e, 'No se pudo guardar la plantilla.')),
    })
  }

  const volver = () => {
    if (sucio && !window.confirm('Hay cambios sin guardar. ¿Salir igual?')) return
    navigate('/payroll/documents')
  }

  const listo = b.name.trim() && b.document_type

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={volver} aria-label="Volver a Documentos"
            className="p-1.5 rounded-lg hover:bg-soft text-content-muted"><ArrowLeft size={18} /></button>
          <h2 className="text-lg font-semibold text-content truncate">
            {esNueva ? 'Nueva plantilla' : b.name || 'Plantilla'}
          </h2>
          {!esNueva && (
            <StatusBadge status={b.status} tone={b.status === 'active' ? 'success' : 'neutral'}
              label={b.status === 'active' ? 'Activa' : b.status === 'archived' ? 'Archivada' : 'Borrador'} />
          )}
          {sucio && <span className="text-xs text-content-muted">Cambios sin guardar</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={!listo} isPending={guardar.isPending}
            onClick={() => enviar(b.status === 'active' ? 'active' : 'draft')}>
            Guardar
          </Button>
          {b.status !== 'active' && (
            <Button disabled={!listo || !b.content_html.trim()} isPending={guardar.isPending}
              onClick={() => enviar('active')}>
              Guardar y activar
            </Button>
          )}
        </div>
      </div>

      {error && <p role="alert" className="text-sm text-danger-content">{error}</p>}
      {avisos.length > 0 && (
        <div role="status" className="flex gap-2 rounded-lg border border-warning-border bg-warning-surface p-3 text-sm text-warning-content">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>Al guardar se quitó contenido que no puede ir en un documento: {avisos.join(' ')}</div>
        </div>
      )}

      <section className="rounded-xl border border-divider bg-surface p-4 sm:p-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField dense label="Nombre" required value={b.name}
            onChange={(e) => cambiar({ name: e.target.value })}
            hint="Con este nombre queda el documento en la carpeta del trabajador." />
          <div>
            <SelectField dense label="Tipo de documento" required value={b.document_type}
              onChange={(e) => cambiar({ document_type: e.target.value })}
              placeholder="Seleccionar el tipo"
              options={tipos.map((t) => ({ value: t.id, label: t.name }))} />
            <p className="text-xs text-content-muted mt-1.5">
              {tipo
                ? <>{tipo.requires_signature ? 'Pide la firma del trabajador' : 'No pide firma'}
                  {' · '}Carpeta: {tipo.default_folder_name || 'Documentos'}. </>
                : 'Del tipo salen la firma y la carpeta. '}
              <Link to="/config/hr-documents" className="text-primary hover:underline">Configurar tipos</Link>
            </p>
          </div>
        </div>
      </section>

      <CamposDelEnvio campos={b.envio_fields} onChange={(envio_fields) => cambiar({ envio_fields })} />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-content">Contenido</h3>
          <SegmentGroup<'editor' | 'previa'> label="Vista" size="sm" value={vista} onChange={setVista}
            options={[{ value: 'editor', label: 'Editor' }, { value: 'previa', label: 'Vista previa' }]} />
        </div>
        {vista === 'editor'
          ? <EditorTexto value={b.content_html} onChange={(content_html) => cambiar({ content_html })} variables={variables} />
          : <VistaPrevia plantillaId={esNueva ? undefined : id} contenido={b.content_html} campos={b.envio_fields} nombre={b.name} />}
      </section>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

// ─── Campos del envío ─────────────────────────────────────────────────────────

function CamposDelEnvio({ campos, onChange }: { campos: CampoDelEnvio[]; onChange: (c: CampoDelEnvio[]) => void }) {
  const [nuevo, setNuevo] = useState('')
  const agregar = () => {
    const rotulo = nuevo.trim()
    if (!rotulo) return
    onChange([...campos, { key: claveDesdeRotulo(rotulo, campos.map((c) => c.key)), label: rotulo, required: true, multiline: false }])
    setNuevo('')
  }
  const editar = (i: number, parcial: Partial<CampoDelEnvio>) =>
    onChange(campos.map((c, j) => (j === i ? { ...c, ...parcial } : c)))

  return (
    <section className="rounded-xl border border-divider bg-surface p-4 sm:p-5 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-content">Campos del envío</h3>
        <p className="text-xs text-content-muted mt-0.5">
          Lo que cambia de un envío a otro y se escribe al generar, por ejemplo el nombre de la
          capacitación o el relator. Así una plantilla sirve para todas las capacitaciones.
        </p>
      </div>
      {campos.length > 0 && (
        <ul className="divide-y divide-divider">
          {campos.map((c, i) => (
            <li key={c.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
              <div className="flex-1 min-w-[200px]">
                <TextField dense label="Rótulo" value={c.label} onChange={(e) => editar(i, { label: e.target.value })} />
              </div>
              <code className="text-xs text-content-muted">{`{{envio.${c.key}}}`}</code>
              <CheckboxField label="Obligatorio" checked={c.required} onChange={(e) => editar(i, { required: e.target.checked })} />
              <CheckboxField label="Varias líneas" checked={c.multiline} onChange={(e) => editar(i, { multiline: e.target.checked })} />
              <button type="button" onClick={() => onChange(campos.filter((_, j) => j !== i))}
                aria-label={`Quitar el campo ${c.label}`} title="Quitar"
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-danger-content">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2 max-w-md">
        <div className="flex-1">
          <TextField dense label="Nuevo campo" value={nuevo} placeholder="Ej.: Relator"
            onChange={(e) => setNuevo(e.target.value)}
            inputProps={{ onKeyDown: (e) => { if (e.key === 'Enter') { e.preventDefault(); agregar() } } }} />
        </div>
        <Button variant="secondary" onClick={agregar} disabled={!nuevo.trim()}><Plus size={14} /> Agregar</Button>
      </div>
    </section>
  )
}

// ─── Vista previa ─────────────────────────────────────────────────────────────

/**
 * Con una persona real y valores de ejemplo para los campos del envío. Muestra
 * lo que se está escribiendo, aunque no se haya guardado. El PDF se abre en el
 * visor de Clobi (en el celular, en una pestaña nueva).
 */
function VistaPrevia({ plantillaId, contenido, campos, nombre }: {
  plantillaId?: string; contenido: string; campos: CampoDelEnvio[]; nombre: string
}) {
  const { data: candidatos = [] } = useCandidatos()
  const { ver, visor } = useVisorPdf()
  const [empleado, setEmpleado] = useState('')
  const [valores, setValores] = useState<Record<string, string>>({})

  useEffect(() => { if (!empleado && candidatos[0]) setEmpleado(candidatos[0].id) }, [candidatos, empleado])

  if (!plantillaId) {
    return (
      <div className="rounded-xl border border-divider bg-surface p-6 text-sm text-content-muted">
        Guardar la plantilla para ver la vista previa con una persona real.
      </div>
    )
  }

  const persona = candidatos.find((c) => c.id === empleado)
  return (
    <div className="rounded-xl border border-divider bg-surface p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-end">
        <SelectField dense label="Ver con los datos de" value={empleado} onChange={(e) => setEmpleado(e.target.value)}
          options={candidatos.map((c) => ({ value: c.id, label: c.name }))} />
        {campos.map((c) => (
          <TextField key={c.key} dense label={c.label} value={valores[c.key] ?? ''}
            placeholder="Valor de ejemplo" multiline={c.multiline} rows={c.multiline ? 2 : undefined}
            onChange={(e) => setValores({ ...valores, [c.key]: e.target.value })} />
        ))}
        <div>
          <Button disabled={!empleado} onClick={() => ver({
            titulo: `${nombre || 'Vista previa'} — ${persona?.name ?? ''}`,
            nombreArchivo: 'vista-previa.pdf',
            cargar: () => pedirVistaPrevia(plantillaId, { employee_id: empleado, envio_values: valores, content_html: contenido }),
          })}>
            <Eye size={14} /> Ver vista previa
          </Button>
        </div>
      </div>
      <p className="text-xs text-content-muted">
        Lo que le falte a esa persona aparece marcado como «falta: …». A quien le falte un dato no se le
        genera el documento hasta completar su ficha.
      </p>
      {visor}
    </div>
  )
}
